<template>
	<div class="DataTable">
		<TopPanel
			:units="units"
			:pagination="localQueryParams"
			:orderBys="orderBys"
			:orderBy="localQueryParams.orderBy"
			:hide-add="hideAdd"
			@toggleAdd="toggleAdd"
			@search="search"
			@orderBy="doOrderBy"
		/>
		<main class="main-view columns">
			<div class="list-container column">
				<div class="title">{{ title }}</div>
				<div class="panel-list-header">
					<div
						:class="{
							'panel-header-item': Boolean(header.text),
							'panel-filler': !Boolean(header.text)
						}"
						v-for="header in headers"
						:key="`${header.name}-${header.value}`"
					>
						{{ header.text }}
					</div>
				</div>

				<progress
					v-if="isPending"
					class="progress is-small is-primary mt-5"
					max="100"
				/>

				<ui-alert class="mt-5" v-if="error" error>{{ error }}</ui-alert>

				<div
					v-else
					class="panel-list"
					v-for="item in items"
					:key="item[itemKey]"
					@click.stop="$emit('on-row-click', { ...item })"
				>
					<div
						:class="`${header.className || 'panel-list-item'}`"
						v-for="header in headers"
						:key="`${header.name}-${header.value}`"
					>
						<slot :name="`item.${header.value}`" :item="item" />
						<div v-if="!$slots[`item.${header.value}`]">
							{{ getItemText(header, item) }}
						</div>
					</div>
				</div>
				<div
					class="panel-list no-hover"
					v-if="localQueryParams.totalCount === 0"
				>
					<p class="panel-list-item">Inga resultat</p>
				</div>
				<Pagination
					v-if="items.length > 0"
					:pagination="localQueryParams"
					@paginate="updatePage"
					@updatePageSize="updatePageSize"
				/>
			</div>
		</main>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getProperty as dotProp } from 'dot-prop';
import TopPanel from '@/components/TopPanel';
import Pagination from '@/components/Pagination';

export default {
	components: {
		TopPanel,
		Pagination
	},

	props: {
		title: {
			type: String,
			default: ''
		},

		hideAdd: {
			type: Boolean,
			default: true
		},

		units: {
			type: Array,
			default: () => []
		},

		queryParams: {
			type: Object,
			default: () => ({})
		},

		orderBys: {
			type: Array,
			default: () => []
		},

		headers: {
			type: Array,
			default: () => []
		},

		itemKey: {
			type: String,
			default: 'id'
		},

		service: {
			type: String,
			required: true
		},

		method: {
			type: String,
			required: true
		}
	},

	data: () => ({
		items: [],
		isPending: false,
		error: '',
		localQueryParams: {
			pageSize: 10,
			totalPages: 1,
			pageNumber: 1,
			orderBy: 'id',
			search: ''
		}
	}),

	watch: {
		queryParams: {
			deep: true,
			handler(to) {
				this.localQueryParams = { ...to };
				this.fetchData();
			}
		}
	},

	computed: {
		...mapGetters('appStore', ['isMobile', 'isCollapsed'])
	},

	created() {
		this.localQueryParams = { ...this.localQueryParams, ...this.queryParams };
		this.fetchData();
	},

	methods: {
		getItemText(header, item) {
			if (header.transform) {
				return header.transform(item);
			}

			return dotProp(item, header.value, '');
		},

		updatePage(pageNumber) {
			this.localQueryParams.pageNumber = pageNumber;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		updatePageSize(pageSize) {
			this.localQueryParams.pageNumber = 1;
			this.localQueryParams.pageSize = pageSize;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		toggleAdd() {
			this.$emit('on-add-click');
		},

		search(searchQuery) {
			this.localQueryParams.pageNumber = 1;
			this.localQueryParams.search = searchQuery;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		doOrderBy(orderBy) {
			this.localQueryParams.orderBy = orderBy;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		async fetchData() {
			this.error = '';

			try {
				const data = await this.$services[this.service][this.method](this.localQueryParams, {
					loading: val => {
						this.isPending = val;
					}
				});

				this.localQueryParams = {
					...this.localQueryParams,
					totalCount: data._pagination.totalCount,
					totalPages: data._pagination.totalPages
				};

				this.items = data;
			} catch (error) {
				this.error = error.message;
			}
		}
	}
};
</script>

<style lang="scss">
@import "@/assets/variables.scss";
@import "@/assets/colors.scss";

.DataTable {
  .panel.info-panel {
    position: absolute;
    pointer-events: none;
    margin-left: -8px;
    top: $navbar-height + 0.75rem;
    background-color: transparent;
    width: calc(100% - 300px);
    color: $white;
    border-radius: 0;
    z-index: 1;

    &.mobile {
      z-index: 4;
      position: fixed;
    }
  }

  .panel.info-panel.narrow {
    width: 100%;
  }

  .info-panel .column {
    max-width: 200px;
    padding: 0;
  }

  .block .label {
    margin-bottom: 2px;
    margin-left: -5px;
    font-size: 10px;
    z-index: 10;
  }

  .panel-block {
    position: relative;
    padding-top: 7px;
    width: 100%;

    .label {
      position: absolute;
      // top: -10px;
      left: 5px;
      z-index: 8;
      font-size: 10px;
    }

    .button {
      z-index: 2;
    }
  }

  .panel-block:only-child {
    margin-top: 0;
  }

  .edge-right {
    position: absolute;
    transform: skew(-30deg);
    right: -20px;
    background-color: transparent;
    width: 40px;
    height: 100%;
  }

  .edge-left {
    position: absolute;
    transform: skew(30deg);
    left: -20px;
    background-color: $light;
    width: 40px;
    height: 100%;
  }

  .panel-block .box {
    background-color: #f5f5f5;
    color: $dark;
    font-size: 12px;
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #acacac;
    box-shadow: none;
  }

  .top-column {
    width: 100%;
    max-width: 172px;
    background: #ffffffe5;
    // box-shadow: 5px 5px 10px #ffffffe5;
    pointer-events: all;

    &.search {
      min-width: 200px;
      max-width: 200px;
    }

    &.dropdown {
      min-width: 172px;
    }

    &.top-column.arrow {
      max-width: 20px;

      i {
        margin-top: 7px;
        pointer-events: auto;
        cursor: pointer;
        z-index: 10;
      }

      i:hover {
        color: $info;
      }
    }
  }

  .panel.info-panel.mobile {
    margin-top: -12px;
    margin-left: -8px;
    width: 100vw;
  }

  .panel-block.mobile {
    margin-left: auto;
    margin-right: 0.3rem;
    width: 53px;
    background: #ffffffe5;
    box-shadow: -5px 5px 10px #ffffffe5;
    pointer-events: all;
  }

  .column.filler {
    max-width: 40px;
  }

  .info-column {
    background: #ffffffe5;
    // box-shadow: 5px 5px 10px #ffffffe5;
    min-width: 172px;
    max-width: 172px;
    pointer-events: all;
  }
}
</style>
